import React, { FunctionComponent, MouseEvent, useCallback, useEffect, useState } from 'react';

import { debounce, first, invert, isNumber, size, sortBy, snakeCase } from 'lodash';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { AccountType, FieldName as TenantFieldName, getTenants, Tenant, TenantType } from 'admin/tenants/Tenants';
import { FieldName as UserFieldName, getUsers, User } from 'admin/users/Users';
import { SUPER_AGENCY_ID, SUPER_TENANT_ID } from 'auth/Auth';
import { Overlay } from 'core/overlay/Overlay';
import { Table } from 'core/table/basic/Table';
import { Operator, SearchRequest, SortOrder } from 'core/util/Search';

import 'common/impersonation-switcher/ImpersonationSwitcher.scss';

export interface ImpersonationSwitcherOptions {
    agencyId: number;
    impersonatedByAgencyId?: number;
}

export interface Props {
    show: boolean;
    onClose: (e: MouseEvent | null, user?: User) => void;
    impersonationSwitcherOptions: ImpersonationSwitcherOptions;
}

enum View {
    Tenants = 'tenants',
    Users = 'users',
}

const ITEMS_PER_PAGE = 10;

export const ImpersonationSwitcher: FunctionComponent<Props> = (props) => {
    const { t } = useTranslation(['common/impersonation-switcher', 'core']);

    const { show, onClose, impersonationSwitcherOptions } = props;
    const { agencyId } = impersonationSwitcherOptions;
    const isSuperAgencyUser = impersonationSwitcherOptions.impersonatedByAgencyId === SUPER_AGENCY_ID;

    const [view, setView] = useState<View>(View.Tenants);
    const [searchQuery, setSearchQuery] = useState<string | undefined>('');
    const [currentPage, setCurrentPage] = useState(0);
    const [hasNext, setHasNext] = useState(false);

    const [tenants, setTenants] = useState<Array<Tenant>>([]);
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [users, setUsers] = useState<Array<User>>([]);

    const getBody = (): JSX.Element => {
        let body: JSX.Element;

        if (view === View.Users) {
            body = (
                <div className="users">
                    <Row>
                        <Col sm={8}>
                            <h4>
                                <Button className="tenants-link" variant="link" onClick={loadTenantsView}>
                                    {t('TENANTS')}
                                </Button>
                                <i className="icon-angle-right" />
                                {tenant ? tenant.name : ' '}
                                <i className="icon-angle-right" />
                                {t('USERS')}
                            </h4>
                        </Col>
                        <Col sm={4}>
                            <Form.Control
                                placeholder={t('core:SEARCH')}
                                value={searchQuery}
                                ref={(input: HTMLInputElement): void => input && input.focus()}
                                onChange={(e): void => setSearchQuery(e.target.value)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Overlay area="users" />
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        <th scope="col">{t('ID')}</th>
                                        <th scope="col">{t('NAME')}</th>
                                        <th scope="col" className="d-none d-md-table-cell">
                                            {t('EMAIL')}
                                        </th>
                                        <th scope="col" className="d-none d-md-table-cell">
                                            {t('ROLE')}
                                        </th>
                                        <th scope="col">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => (
                                        <tr key={user.id}>
                                            <th scope="row">{user.id}</th>
                                            <td>{user.displayName}</td>
                                            <td className="email d-none d-md-table-cell">{user.email}</td>
                                            <td className="d-none d-md-table-cell">
                                                {sortBy(user.roles, 'name').map((role) => (
                                                    <div key={role.id}>{role.name}</div>
                                                ))}
                                            </td>
                                            <td className="actions">
                                                <Button
                                                    className="btn-xs"
                                                    variant="primary"
                                                    onClick={(): void => onClose(null, user)}
                                                >
                                                    <i className="icon-user" /> {t('IMPERSONATE')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div className="text-right">
                                <Button
                                    className={currentPage === 0 ? 'disabled' : ''}
                                    size="sm"
                                    variant="secondary"
                                    onClick={(): void => loadPrevious()}
                                >
                                    {t('core:PREVIOUS')}
                                </Button>
                                <Button
                                    className={!hasNext ? 'disabled ml-2' : 'ml-2'}
                                    size="sm"
                                    variant="secondary"
                                    onClick={(): void => loadNext()}
                                >
                                    {t('core:NEXT')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            );
        } else {
            body = (
                <div className="tenants">
                    <Row>
                        <Col sm={8}>
                            <h4>{t('TENANTS')}</h4>
                        </Col>
                        <Col sm={4} className="text-right">
                            <Form.Control
                                placeholder={t('core:SEARCH')}
                                value={searchQuery}
                                ref={(input: HTMLInputElement): void => input && input.focus()}
                                onChange={(e): void => setSearchQuery(e.target.value)}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Overlay area="tenants" />
                            <Table bordered hover>
                                <thead>
                                    <tr>
                                        {isSuperAgencyUser ? <th scope="col">{t('AGENCY')}</th> : null}
                                        <th scope="col">{t('ID')}</th>
                                        <th scope="col">{t('TENANT')}</th>
                                        <th scope="col" className="d-none d-md-table-cell">
                                            {t('COMPANY_TYPE')}
                                        </th>
                                        <th scope="col" className="d-none d-md-table-cell">
                                            {t('ACCOUNT_TYPE')}
                                        </th>
                                        <th scope="col">{t('IMPERSONATE')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenants.map((tenant) => (
                                        <tr key={tenant.id}>
                                            {isSuperAgencyUser ? <th scope="row">{tenant.agencyID}</th> : null}
                                            <th scope="row">{tenant.id}</th>
                                            <td>{tenant.name}</td>
                                            <td className="d-none d-md-table-cell">
                                                {getTenantTypeLabel(tenant.tenantType)}
                                            </td>
                                            <td className="d-none d-md-table-cell">
                                                {getAccountTypeLabel(tenant.accountType)}
                                            </td>
                                            <td className="actions">
                                                <Button
                                                    className="btn-xs"
                                                    variant="primary"
                                                    onClick={(): void => impersonateSystemUser(tenant)}
                                                >
                                                    {t('SYSTEM_USER')}
                                                </Button>
                                                <Button
                                                    className="btn-xs ml-2"
                                                    variant="secondary"
                                                    onClick={(): void => loadUsersView(tenant)}
                                                >
                                                    {t('OTHER_USER')}
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div className="text-right">
                                <Button
                                    className={currentPage === 0 ? 'disabled' : ''}
                                    size="sm"
                                    variant="secondary"
                                    onClick={(): void => loadPrevious()}
                                >
                                    {t('core:PREVIOUS')}
                                </Button>
                                <Button
                                    className={!hasNext ? 'disabled ml-2' : 'ml-2'}
                                    size="sm"
                                    variant="secondary"
                                    onClick={(): void => loadNext()}
                                >
                                    {t('core:NEXT')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            );
        }

        return body;
    };

    const search = (searchQuery: string | undefined, view: View, tenant?: Tenant | null): void => {
        if (view === View.Users && tenant) {
            loadUsers(tenant, 0, searchQuery);
        } else {
            loadTenants(0, searchQuery);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceSearch = useCallback(debounce(search, 100), []);

    useEffect(() => {
        if (show) {
            debounceSearch(searchQuery, view, tenant);
        }
    }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadPrevious = (): void => {
        const previousPage = currentPage - 1;

        if (previousPage >= 0) {
            setCurrentPage(previousPage);

            if (view === View.Users && tenant) {
                loadUsers(tenant, previousPage * ITEMS_PER_PAGE, searchQuery);
            } else {
                loadTenants(previousPage * ITEMS_PER_PAGE, searchQuery);
            }
        }
    };

    const loadNext = (): void => {
        const nextPage = currentPage + 1;

        setCurrentPage(nextPage);

        if (view === View.Users && tenant) {
            loadUsers(tenant, nextPage * ITEMS_PER_PAGE, searchQuery);
        } else {
            loadTenants(nextPage * ITEMS_PER_PAGE, searchQuery);
        }
    };

    const loadTenantsView = (): void => {
        setSearchQuery('');
        setCurrentPage(0);
        setTenant(null);
        setUsers([]);
        setView(View.Tenants);

        loadTenants();
    };

    const loadTenants = (start?: number, searchQuery?: string): void => {
        const search: SearchRequest = {
            filters: [
                {
                    name: TenantFieldName.Active,
                    values: ['true'],
                },
                {
                    name: TenantFieldName.Id,
                    values: [String(SUPER_TENANT_ID)],
                    operator: Operator.NotEqual,
                },
            ],
            sorts: [
                {
                    name: TenantFieldName.TenantId,
                    sortOrder: SortOrder.Asc,
                },
            ],
            projections: [
                TenantFieldName.Id,
                TenantFieldName.TenantId,
                TenantFieldName.AgencyId,
                TenantFieldName.Name,
                '__tenant_tenantType__', // TenantFieldName.TenantType,
                '__tenant_accountType__', // TenantFieldName.AccountType,
                TenantFieldName.Active,
            ],
            count: ITEMS_PER_PAGE + 1,
            start: isNumber(start) ? start : 0,
        };
        const args = {
            runAsAgencyUser: true,
            runAsSuperAgencyUser: isSuperAgencyUser,
        };

        if (!isSuperAgencyUser) {
            search.filters.push({
                name: TenantFieldName.AgencyId,
                values: [String(agencyId)],
            });
        }

        if (searchQuery && size(searchQuery)) {
            search.filters.push({
                name: TenantFieldName.Search,
                values: [searchQuery],
                operator: Operator.iLike,
            });
        }

        (async (): Promise<any> => {
            try {
                const tenants = await trackPromise(getTenants(agencyId, search, args), 'tenants');

                if (size(tenants) > ITEMS_PER_PAGE) {
                    setHasNext(true);
                    tenants.pop();
                } else {
                    setHasNext(false);
                }

                setTenants(tenants);
            } catch {}
        })();
    };

    const tenantTypeByValue = invert(TenantType);

    const getTenantTypeLabel = (tenantType: TenantType): string => {
        const translationKey = snakeCase(tenantTypeByValue[tenantType]).toUpperCase();

        return t(translationKey);
    };

    const accountTypeByValue = invert(AccountType);

    const getAccountTypeLabel = (accountType: AccountType): string => {
        const translationKey = accountTypeByValue[accountType].toUpperCase();

        return t(translationKey);
    };

    const loadUsersView = (tenant: Tenant): void => {
        setSearchQuery('');
        setCurrentPage(0);
        setTenant(tenant);
        setTenants([]);
        setView(View.Users);

        loadUsers(tenant);
    };

    const loadUsers = (tenant: Tenant, start?: number, searchQuery?: string): void => {
        const search: SearchRequest = {
            filters: [
                {
                    name: UserFieldName.Active,
                    values: ['true'],
                },
            ],
            sorts: [
                {
                    name: UserFieldName.UserId,
                    sortOrder: SortOrder.Asc,
                },
            ],
            projections: [
                UserFieldName.Active,
                UserFieldName.AgencyId,
                UserFieldName.DisplayName,
                UserFieldName.Email,
                UserFieldName.RoleIds,
                UserFieldName.System,
                UserFieldName.TenantId,
                UserFieldName.UserId,
            ],
            count: ITEMS_PER_PAGE + 1,
            start: isNumber(start) ? start : 0,
        };
        const args = {
            basic: false,
            includeRoles: true,
            runAsAgencyUser: true,
        };

        if (searchQuery && size(searchQuery)) {
            search.filters.push({
                name: UserFieldName.Search,
                values: [searchQuery],
                operator: Operator.iLike,
            });
        }

        (async (): Promise<any> => {
            try {
                const users = await trackPromise(getUsers(tenant.agencyID, tenant.id, search, args), 'users');

                if (size(users) > ITEMS_PER_PAGE) {
                    setHasNext(true);
                    users.pop();
                } else {
                    setHasNext(false);
                }

                setUsers(users);
            } catch {}
        })();
    };

    const impersonateSystemUser = (tenant: Tenant): void => {
        const search = {
            filters: [
                {
                    name: UserFieldName.System,
                    values: ['true'],
                },
            ],
        };
        const args = {
            runAsAgencyUser: true,
        };

        (async (): Promise<any> => {
            try {
                const systemUsers = await trackPromise(getUsers(tenant.agencyID, tenant.id, search, args), 'tenants');

                onClose(null, first(systemUsers));
            } catch {}
        })();
    };

    return (
        <Modal className="r4e-impersonation-switcher" size="lg" show={show} onHide={onClose} onShow={loadTenants}>
            <Modal.Header closeButton>
                <Modal.Title>{t('SWITCH_TENANT')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>{getBody()}</Modal.Body>

            <Modal.Footer>
                <Button variant="link" onClick={onClose}>
                    {t('core:CANCEL')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
